import { Flame, Sparkles } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import type { Discipline, OverplayedSong, VersatileArtist } from '@/hooks/useDashboardChannel'
import { PlayButton } from '@/components/player/PlayButton'
import { usePlayerState } from '@/components/player/PlayerProvider'
import { songKey, songTrack } from '@/components/player/tracks'
import { workoutNouns } from '@/lib/discipline'
import { initials } from '@/lib/format'
import { CountUp } from '@/components/dashboard/fx/CountUp'
import { Reveal } from '@/components/dashboard/fx/Reveal'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'

interface VinylFeatureProps {
  song: OverplayedSong | null | undefined
  artist: VersatileArtist | null | undefined
  discipline: Discipline
}

export function VinylFeature({ song, artist, discipline }: VinylFeatureProps) {
  const { plural } = workoutNouns(discipline)
  const player = usePlayerState()
  const playing = Boolean(song && player.track?.key === songKey(song.id) && player.status === 'playing')

  if (!song && !artist) {
    return (
      <p className="rounded-3xl border border-dashed border-border py-16 text-center text-muted-foreground">
        Not enough {plural} synced yet to crown a most-overplayed track.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {song && (
        <div
          className={`group relative isolate overflow-hidden rounded-[2rem] border border-border bg-card/40 p-6 sm:p-10 lg:p-14 ${playing ? 'vinyl-playing' : ''}`}
        >
          {song.album_image_url && (
            <img
              src={song.album_image_url}
              alt=""
              aria-hidden
              className="absolute inset-0 -z-10 size-full scale-150 object-cover opacity-30 blur-3xl"
            />
          )}
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-background/10 via-background/40 to-background/80" />

          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[auto_1fr] lg:gap-16">
            {/* Sleeve + record. The record peeks out from behind the sleeve
                and slides further out on hover. */}
            <div className="relative mx-auto flex w-[calc(min(56vw,20rem)*1.5)] lg:mx-0">
              <div className="relative z-10 aspect-square w-[min(56vw,20rem)] shrink-0 overflow-hidden rounded-xl shadow-2xl shadow-black/50 ring-1 ring-white/10">
                {song.album_image_url ? (
                  <img src={song.album_image_url} alt="" className="size-full object-cover" />
                ) : (
                  <div className="flex size-full items-center justify-center bg-gradient-to-br from-primary/30 to-[#eb6834]/30">
                    <Flame className="size-16 text-primary" />
                  </div>
                )}
                <PlayButton
                  variant="solid"
                  track={songTrack(song)}
                  className="absolute bottom-3 left-3 size-12 sm:size-14"
                />
              </div>
              <div
                className={`absolute left-[calc(min(56vw,20rem)*0.38)] top-[2%] aspect-square w-[calc(min(56vw,20rem)*0.96)] transition-transform duration-700 ease-out group-hover:translate-x-[16%] ${playing ? 'translate-x-[16%]' : ''}`}
              >
                <div className="vinyl-disc relative size-full rounded-full shadow-2xl shadow-black/60">
                  <div className="absolute left-1/2 top-1/2 size-[34%] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full ring-2 ring-black/60">
                    {song.album_image_url ? (
                      <img src={song.album_image_url} alt="" className="size-full object-cover" />
                    ) : (
                      <div className="size-full bg-primary" />
                    )}
                    <span className="absolute left-1/2 top-1/2 size-[9%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-black" />
                  </div>
                </div>
                <div className="vinyl-sheen pointer-events-none absolute inset-0 rounded-full" />
              </div>
            </div>

            <div className="flex min-w-0 flex-col gap-5 lg:pl-8">
              <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-primary">
                <Flame className="size-4" />
                Most overplayed track
              </span>
              <h3 className="font-display text-[clamp(2.25rem,6.5vw,5.75rem)] font-semibold leading-[0.95] tracking-tighter [text-wrap:balance]">
                {song.title}
              </h3>
              <p className="font-accent text-3xl italic text-muted-foreground">{song.artists.join(', ')}</p>

              <div className="mt-4 flex items-end gap-4 border-t border-border/70 pt-6">
                <CountUp
                  value={song.ride_count}
                  className="font-display text-[clamp(3.5rem,9vw,7rem)] font-semibold leading-[0.85] tracking-tighter text-gradient"
                />
                <span className="pb-1 text-sm text-muted-foreground">
                  {plural}
                  <br />
                  and counting
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {artist && (
        <Reveal>
          <SpotlightCard className="flex flex-col items-start gap-6 p-6 sm:flex-row sm:items-center sm:p-8">
            <Avatar className="size-20 ring-2 ring-primary/40 ring-offset-4 ring-offset-background">
              <AvatarImage src={artist.image_url ?? undefined} alt="" />
              <AvatarFallback className="font-display text-xl">{initials(artist.name)}</AvatarFallback>
            </Avatar>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-primary">
                <Sparkles className="size-4" />
                The chameleon
              </span>
              <span className="truncate font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                {artist.name}
              </span>
              <span className="text-sm text-muted-foreground">Most versatile artist -- shows up in more genres than anyone</span>
            </div>
            <div className="flex items-baseline gap-2">
              <CountUp value={artist.genre_count} className="font-display text-6xl font-semibold tracking-tighter" />
              <span className="text-sm text-muted-foreground">genres</span>
            </div>
          </SpotlightCard>
        </Reveal>
      )}
    </div>
  )
}
